<script>
import MetaData from '@/components/common/metaData/MetaData.vue'
import { FileUploadStatus } from '@/enums/FileUploadStatus'
import UploadDialog from '../UploadDialog.vue'
export default {
  components: {
    UploadDialog,
    MetaData
  },
  props: {
    fileElement: { type: File },
    deleteFile: { type: Function },
    letterCategory: { type: String },
    index: { type: Number }
  },
  data: () => ({
    metaDataKey: 1,
    additionalString: '',
    uploaded: false,
    deleting: false,
    cloudIcon: '',
    cloudIconColor: '',
    toolTipContent: '',
    dialogFileList: [],
    showUploadDialog: false,
    fileUploadStatus: FileUploadStatus.Open,
    needUserSettings: false
  }),
  watch: {
    // important for "PaymentOpen.vue"
    fileUploadStatus(uploadStatus) {
      this.changeFileUploadStatus(uploadStatus)
      switch (uploadStatus) {
        case FileUploadStatus.Pending:
          this.cloudIcon = 'mdi-cloud-sync'
          this.cloudIconColor = this.$t(
            'component.upload.pdfListElement.cloud.icon.color.loading')
          this.toolTipContent = this.$t('component.upload.pdfListElement.tool.tip.loading')
          break
        case FileUploadStatus.Uploaded:
          this.cloudIcon = 'mdi-cloud-upload'
          this.cloudIconColor = this.$t('component.upload.pdfListElement.cloud.icon.color.uploaded')
          this.toolTipContent = this.$t('component.upload.pdfListElement.tool.tip.uploaded')
          break
        case FileUploadStatus.Failed:
          this.cloudIcon = 'mdi-cloud-download'
          this.cloudIconColor = this.$t('component.upload.pdfListElement.cloud.icon.color.failed')
          this.toolTipContent = this.$t('component.upload.pdfListElement.tool.tip.failed')
          break
      }
    }
  },
  created() {
    this.fileUploadStatus = this.fileElement.needSettings
      ? FileUploadStatus.Pending
      : FileUploadStatus.Uploaded
  },
  methods: {
    changeFileUploadStatus(uploadStatus) {
      this.fileElement.fileUploadStatus = uploadStatus
      this.$emit('changed', {
        file: this.fileElement,
        index: this.index
      })
    },
    deleteComponent() {
      switch (this.fileUploadStatus) {
        // delete file from Server
        case FileUploadStatus.Uploaded:
          this.deleting = true
          this.fileUploadStatus = FileUploadStatus.Pending
          return this.$uploadService
            .deleteFile({ uploadFileIds: [this.fileElement.id] })
            .then(() => {
              this.changeFileUploadStatus(FileUploadStatus.Uploaded)
              this.deleteFile()
            })
            .catch(() => {})
            .finally(() => {
              this.deleting = false
            })

        // delete file from list
        case FileUploadStatus.Failed:
        case FileUploadStatus.Pending:
          this.deleteFile()
          break
        default:
          break
      }
    },
    async setMetaData() {
      return new Promise(async resolve => {
        if (this.fileElement.needSettings) {
          await this.$uploadService
            .editMetaData(
              this.fileElement.id,
              this.generateFileMetaDataObject(this.fileElement)
            )
            .then(() => {
              this.uploaded = true
              this.fileUploadStatus = FileUploadStatus.Uploaded
              resolve()
            })
        } else if (!this.fileElement.uploaded) {
          this.fileUploadStatus = FileUploadStatus.Failed
          resolve()
        } else {
          // if files have metadata und successfully uploaded
          // then show no settings but just correct upload status
          this.fileUploadStatus = FileUploadStatus.Uploaded
          resolve()
        }
      })
    },
    changeMetaData(printSettings) {
      this.fileUploadStatus = FileUploadStatus.Pending
      return this.$uploadService
        .editMetaData(
          this.fileElement.id,
          this.generateFileMetaDataObject(printSettings)
        )
        .then(response => {
          // common-settings
          this.fileElement.LetterType = response.letterType
          this.fileElement.PaperTypes = response.paperTypes
          this.fileElement.PrintMode = printSettings.PrintMode
          this.fileElement.ColorMode = printSettings.ColorMode
          this.fileElement.FoldType = printSettings.FoldType
          this.fileElement.CostCenter = printSettings.CostCenter
          // series-settings
          this.fileElement.splitAfterNPages = printSettings.splitAfterNPages
          this.fileElement.splitAfterTextPattern =
            printSettings.splitAfterTextPattern
          this.fileElement.SplitType = printSettings.SplitType
          this.fileElement.ignoreCase = printSettings.ignoreCase
          this.fileElement.detectionArea = {
            positionX: printSettings.detectionArea.positionX,
            positionY: printSettings.detectionArea.positionY,
            width: printSettings.detectionArea.width,
            height: printSettings.detectionArea.height
          }
          this.showUploadDialog = false
        })
        .catch(() => {})
        .finally(() => {
          this.fileUploadStatus = FileUploadStatus.Uploaded
          this.metaDataKey++ // force re-render
        })
    },
    generateFileMetaDataObject(fileObject) {
      // used for all files
      let fileMetaData = {
        fileid: fileObject.id,
        PrintMode: fileObject.PrintMode,
        ColorMode: fileObject.ColorMode,
        FoldType: fileObject.FoldType,
        isSerialLetter: fileObject.isSerialLetter,
        ignoreCase: fileObject.ignoreCase,
        CostCenter: fileObject.CostCenter
      }
      // used for serialFiles
      let fileMetaDataExtended = {
        detectionArea: {
          positionX: fileObject.detectionArea.positionX,
          positionY: fileObject.detectionArea.positionY,
          width: fileObject.detectionArea.width,
          height: fileObject.detectionArea.height
        },
        splitAfterNPages: fileObject.splitAfterNPages || 0,
        SplitType: fileObject.SplitType,
        splitAfterTextPattern: fileObject.splitAfterTextPattern || ''
      }
      return Object.assign(
        fileMetaData,
        this.fileElement.isSerialLetter ? fileMetaDataExtended : {}
      )
    }
  }
}
</script>
<template>
  <span>
    <v-list-item
      :id="fileElement.id"
      :key="fileElement.name"
      :ripple="false"
      class="default_cursor">
      <v-tooltip top>
        <template #activator="{ on }">
          <v-list-item-content name="upload_status" class="mr-2 upload_status">
            <v-icon :color="cloudIconColor" v-on="on">{{ cloudIcon }} </v-icon>
          </v-list-item-content>
        </template>
        <span>{{ toolTipContent }}</span>
      </v-tooltip>
      <v-list-item-content name="file_description">
        <v-list-item-title class="mb-1">
          {{ fileElement.name }}
        </v-list-item-title>
        <v-list-item-subtitle name="secondarySubTitle" class="text--secondary">
          <MetaData
            :key="metaDataKey"
            :meta-data="JSON.parse(JSON.stringify(fileElement))"
            small />
        </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-list-item-action-text v-if="uploaded">
          <div align="right">
            <v-btn
              name="change"
              dark
              icon
              small
              color="primary"
              @click="showUploadDialog = true">
              <v-icon grey>mdi-cog</v-icon>
            </v-btn>
          </div>
        </v-list-item-action-text>
      </v-list-item-action>
      <v-list-item-action>
        <v-list-item-action-text>
          <div align="right">
            <v-btn
              name="delete"
              dark
              icon
              small
              color="error"
              :loading="deleting"
              @click="deleteComponent">
              <v-icon dark>mdi-delete-forever</v-icon>
            </v-btn>
          </div>
        </v-list-item-action-text>
      </v-list-item-action>
    </v-list-item>
    <UploadDialog
      :show-dialog.sync="showUploadDialog"
      :letter-category="letterCategory"
      :files="[fileElement]"
      is-edit
      @submit="changeMetaData($event)"
    /></span>
</template>
<style lang="scss" scoped>
.default_cursor {
  cursor: default;
}
.upload_status {
  max-width: 35px;
}
</style>
